// /context/CookiesContext.jsx
import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {applyConsentToGtag, bootstrapConsentDefaults, enableGA} from "../../lib/gtag.js";
import {deleteGaCookies, readConsentCookie, writeConsentCookie} from "../../lib/cookies.js";

const CookiesContext = createContext(null);
export function useCookies() {
    const ctx = useContext(CookiesContext);
    if (!ctx) throw new Error("useCookies must be used within <CookiesProvider>");
    return ctx;
}

const DEFAULT_PREFS = {
    required: true,
    analytic: false,
    advertisement: false,
    personalization: false,
};

export default function CookiesProvider({measurementId, children}) {
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);
    const [decided, setDecided] = useState(false); // czy user już podjął decyzję

    // Blokuj scroll pod overlayem, kiedy nie zdecydowano
    useEffect(() => {
        if (!decided) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = prev; };
        }
    }, [decided]);

    // Domyślne DENIED w Consent Mode od razu po starcie
    useEffect(() => {
        bootstrapConsentDefaults();
    }, []);

    // Odczyt z cookie
    useEffect(() => {
        const saved = readConsentCookie();
        if (saved && typeof saved === 'object') {
            const merged = {...DEFAULT_PREFS, ...saved};
            setPrefs(merged);
            setDecided(true);
            applyConsentToGtag(merged);
            if (merged.analytic) {
                enableGA(measurementId);
            }
        }
    }, [measurementId]);

    // Akcje
    const acceptAll = async () => {
        const next = {
            required: true,
            analytic: true,
            advertisement: true,
            personalization: true,
        };
        writeConsentCookie(next);
        setPrefs(next);
        setDecided(true);
        applyConsentToGtag(next);
        await enableGA(measurementId);
    };

    const savePreferences = async (partial) => {
        const next = {
            ...DEFAULT_PREFS,
            ...prefs,
            ...partial,
            required: true, // nie do wyłączenia
        };
        writeConsentCookie(next);
        setPrefs(next);
        setDecided(true);
        applyConsentToGtag(next);

        if (next.analytic) {
            await enableGA(measurementId);
        } else {
            deleteGaCookies();
        }
    };

    const revokeAll = async () => {
        await savePreferences({
            analytic: false,
            advertisement: false,
            personalization: false
        });
    };

    const value = useMemo(() => ({
        prefs,
        decided,
        acceptAll,
        savePreferences,
        revokeAll,
    }), [prefs, decided]);

    return (
        <CookiesContext.Provider value={value}>
            {children}
        </CookiesContext.Provider>
    );
}
